import {
  UseQueryOptions, UseQueryResult, useQuery,
} from 'react-query';
import httpClient from '../../core/httpClient';
import { QUERY_OPTIONS } from '../../constants';
import {
  YardiSyncAuditEvent,
  YardiSyncException,
  YardiSyncProperty,
  YardiSyncRun,
  YardiSyncRunDetail,
  YardiSyncSession,
  YardiSyncTaskStatus,
} from '../../types/yardiSync';

export const useYardiSyncSessionQuery = (): UseQueryResult<YardiSyncSession, Error> => useQuery<
YardiSyncSession, Error>(
  ['yardi-sync-session'],
  async () => {
    const response = await httpClient.get('/yardi-sync/session');
    return response.data;
  },
  QUERY_OPTIONS,
);

export const useYardiSyncPropertiesQuery = (): UseQueryResult<YardiSyncProperty[], Error> => useQuery<
YardiSyncProperty[], Error>(
  ['yardi-sync-properties'],
  async () => {
    const response = await httpClient.get('/yardi-sync/properties');
    return response.data;
  },
  QUERY_OPTIONS,
);

export const useYardiSyncRunsQuery = (
  limit: number,
  offset: number,
): UseQueryResult<YardiSyncRun[], Error> => useQuery<YardiSyncRun[], Error>(
  ['yardi-sync-runs', limit, offset],
  async () => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    const response = await httpClient.get(`/yardi-sync/runs?${params.toString()}`);
    return response.data;
  },
  QUERY_OPTIONS,
);

export const useYardiSyncRunDetailQuery = (
  runId: number | undefined,
  options?: UseQueryOptions<YardiSyncRunDetail, Error>,
): UseQueryResult<YardiSyncRunDetail, Error> => useQuery<YardiSyncRunDetail, Error>(
  ['yardi-sync-run', runId],
  async () => {
    const response = await httpClient.get(`/yardi-sync/runs/${runId}`);
    return response.data;
  },
  {
    ...QUERY_OPTIONS,
    enabled: !!runId,
    ...options,
  },
);

export const useYardiSyncExceptionsQuery = (
  runId: number | undefined,
  limit: number,
  offset: number,
  propertyCode?: string,
): UseQueryResult<YardiSyncException[], Error> => useQuery<YardiSyncException[], Error>(
  ['yardi-sync-exceptions', runId, propertyCode, limit, offset],
  async () => {
    const params = new URLSearchParams();
    if (propertyCode) {
      params.append('property_code', propertyCode);
    }
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    const response = await httpClient.get(`/yardi-sync/runs/${runId}/exceptions?${params.toString()}`);
    return response.data;
  },
  {
    ...QUERY_OPTIONS,
    enabled: !!runId,
  },
);

export const useYardiSyncAuditEventsQuery = (
  runId: number | undefined,
  limit: number,
  offset: number,
  propertyCode?: string,
  buildingCode?: string,
  entityType?: string,
  action?: string,
): UseQueryResult<YardiSyncAuditEvent[], Error> => useQuery<YardiSyncAuditEvent[], Error>(
  ['yardi-sync-audit-events', runId, propertyCode, buildingCode, entityType, action, limit, offset],
  async () => {
    const params = new URLSearchParams();
    if (propertyCode) {
      params.append('property_code', propertyCode);
    }
    if (buildingCode) {
      params.append('building_code', buildingCode);
    }
    if (entityType) {
      params.append('entity_type', entityType);
    }
    if (action) {
      params.append('action', action);
    }
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    const response = await httpClient.get(`/yardi-sync/runs/${runId}/audit-events?${params.toString()}`);
    return response.data;
  },
  {
    ...QUERY_OPTIONS,
    enabled: !!runId,
  },
);

export const useYardiSyncTaskStatusQuery = (
  taskId: string | null,
  options?: UseQueryOptions<YardiSyncTaskStatus, Error>,
): UseQueryResult<YardiSyncTaskStatus, Error> => useQuery<YardiSyncTaskStatus, Error>(
  ['yardi-sync-task', taskId],
  async () => {
    const response = await httpClient.get(`/yardi-sync/tasks/${taskId}`);
    return response.data;
  },
  {
    enabled: !!taskId,
    ...options,
  },
);
